import { defaultImageLibraryLocation, ImageLibraryStorageType } from './constants'
import { Blueprint } from './model'

// One configured image library store as shown in the blueprint editor.
export interface StoreRow {
  // The store value sent to the backend: a Cinder volume-type name, or a filesystem path.
  value: string
  type: ImageLibraryStorageType
  isDefault: boolean
}

// CBP-FR-001: the store row a fresh (never-saved) blueprint starts with, so the Image Library
// section shows the default location up front instead of an empty editor. It mirrors what appears
// after the first save today: a single Local File System store at the default path, marked default.
export const defaultStoreRows = (): StoreRow[] => [
  {
    value: defaultImageLibraryLocation,
    type: ImageLibraryStorageType.LOCAL_FILESYSTEM,
    isDefault: true,
  },
]

// A value starting with "/" is a filesystem path store; anything else is a Cinder volume type.
// For a path store we can only tell "shared" from "local" via the blueprint's shared flag, since
// the value itself is just a path.
const typeForValue = (value: string, shared: boolean): ImageLibraryStorageType => {
  if (!value?.startsWith('/')) return ImageLibraryStorageType.VOLUME_TYPE
  return shared
    ? ImageLibraryStorageType.SHARED_FILESYSTEM
    : ImageLibraryStorageType.LOCAL_FILESYSTEM
}

// Turn a blueprint into editor rows. Prefers the multi-store list; falls back to the legacy
// single-store imageLibraryStorage value so existing clusters open cleanly.
export const blueprintToStoreRows = (bp: Partial<Blueprint>): StoreRow[] => {
  const shared = !!bp?.imageLibrarySharedStorage
  if (bp?.imageLibraryStores?.length) {
    const rows = bp.imageLibraryStores.map((value) => ({
      value,
      type: typeForValue(value, shared),
      isDefault: value === bp.defaultImageLibraryStore,
    }))
    // A store list must always have exactly one default; if the saved default is missing or
    // unmatched, fall back to the first store so the editor never shows a defaultless library.
    if (!rows.some((row) => row.isDefault)) {
      rows[0].isDefault = true
    }
    return rows
  }
  if (bp?.imageLibraryStorage) {
    return [
      {
        value: bp.imageLibraryStorage,
        type: typeForValue(bp.imageLibraryStorage, shared),
        isDefault: true,
      },
    ]
  }
  return []
}

// Map the blueprint's configured store values to the store ids Glance actually reports on an image.
// A filesystem store is saved in the blueprint as its path (e.g. "/var/lib/glance/images"), but the
// backend normalises a path store to the id "file", which is what shows up in an image's locations.
// Volume-type stores already match Glance's id, so they pass through unchanged. Deduped because two
// filesystem paths would both normalise to "file".
// `values` is coalesced with `?? []` (not a default param) because the backend sends `null` — not
// `undefined` — for a blueprint that predates multi-store, and a default param only fires on
// `undefined`. Passing that `null` straight to `.map` is what crashed the Images page.
export const toGlanceStoreIds = (values?: string[] | null): string[] => [
  ...new Set(
    (values ?? []).map((value) => (value?.startsWith('/') ? 'file' : value)).filter(Boolean),
  ),
]

// The blueprint's configured store values, preferring the multi-store list and falling back to the
// legacy single-store `imageLibraryStorage` — so an existing (pre-PCD-7717) blueprint, where
// `imageLibraryStores` is null/absent, still reports its one store instead of looking unconfigured.
// Mirrors the same fallback blueprintToStoreRows uses for the editor.
export const blueprintStoreValues = (bp?: Partial<Blueprint> | null): string[] => {
  if (bp?.imageLibraryStores?.length) return bp.imageLibraryStores
  if (bp?.imageLibraryStorage) return [bp.imageLibraryStorage]
  return []
}

// Store values picked by every row other than `index` (used to keep selections unique — a volume
// type already chosen in another store must not be selectable again).
export const valuesUsedByOtherRows = (rows: StoreRow[], index: number): Set<string> =>
  new Set(
    rows
      .filter((_, i) => i !== index)
      .map((row) => row.value)
      .filter(Boolean),
  )

// Indexes of store rows whose value is empty — a volume-type store with nothing selected, or a
// filesystem store with a blank path. These are the required-field violations that must block a
// blueprint save (restores the pre-multistore "Location / Volume Type is required" validation).
export const invalidStoreRowIndexes = (rows: StoreRow[]): number[] =>
  rows.reduce<number[]>((acc, row, i) => (row.value?.trim() ? acc : [...acc, i]), [])

// Serialize editor rows into the blueprint payload (a list of store values plus the default).
export const storeRowsToPayload = (
  rows: StoreRow[],
): { imageLibraryStores: string[]; defaultImageLibraryStore: string } => {
  const defaultRow = rows.find((r) => r.isDefault) ?? rows[0]
  return {
    imageLibraryStores: rows.map((r) => r.value),
    defaultImageLibraryStore: defaultRow?.value,
  }
}
